import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { SprintEntity } from '../utils/types';

export type SprintPage<TItem> = {
  sprint: SprintEntity;
  /** Undefined until this sprint has been asked for and its rows have arrived. */
  items?: TItem[];
  /** The request for this sprint came back an error. Told apart from "not here yet" on purpose: a
   * placeholder that never resolves says nothing about why. */
  hasFailed: boolean;
  /** Ask for this sprint again, for the button the failure offers. */
  retry: () => void;
};

/**
 * One query per sprint of the open year page, each held back until the section that shows it comes
 * near the viewport.
 *
 * The list itself is laid out in full from the start (see SprintSection), so this decides only when
 * the rows behind each placeholder are fetched, never how tall the page is. Every sprint keeps its
 * own cache entry, so a year already opened comes back without asking again, and the current-sprint
 * tab reuses what the past tab loaded.
 */
export const useSprintPages = <TItem>(
  sprints: SprintEntity[] | undefined,
  queryFor: (sprint: SprintEntity) => { queryKey: readonly unknown[]; queryFn: () => Promise<TItem[]> },
  enabled: boolean,
): {
  pages: SprintPage<TItem>[];
  isFetching: boolean;
  ask: (sprintId: string) => void;
} => {
  const [asked, setAsked] = useState<Record<string, true>>({});

  // Another year (or another filter) is another list: what was asked for on the old one says nothing
  // about this one, and the sections will ask again as they come into view.
  const sprintIds = sprints?.map(sprint => sprint.id).join(',');
  useEffect(() => {
    setAsked({});
  }, [sprintIds]);

  const ask = useCallback((sprintId: string) => {
    setAsked(current => (current[sprintId] ? current : { ...current, [sprintId]: true }));
  }, []);

  const results = useQueries({
    queries: (sprints ?? []).map(sprint => ({
      ...queryFor(sprint),
      enabled: enabled && !!asked[sprint.id],
      // The numbers for a sprint that has ended do not change while you scroll past them.
      staleTime: 60 * 1000,
    })),
  });

  return {
    pages: (sprints ?? []).map((sprint, index) => ({
      sprint,
      items: results[index]?.data,
      hasFailed: !!results[index]?.isError,
      retry: () => {
        results[index]?.refetch();
      },
    })),
    isFetching: results.some(result => result.isFetching),
    ask,
  };
};
